"use client";

import React from "react";
import { FileText, BookOpen, CircleCheck as CheckCircle2, GraduationCap, ArrowRight, Download, Search, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface ContentFormatItem {
  id: string;
  title: string;
  category: string;
  count: string;
  description: string;
  topics: string;
  ctaText: string;
  href: string;
  iconName: "FileText" | "BookOpen" | "CheckCircle2" | "GraduationCap";
}
const contentFormats: ContentFormatItem[] = [{
  id: "app-notes",
  title: "Application Notes",
  category: "Methodology & Protocols",
  count: "45+ Documents",
  description: "Method development summaries, NIR and Raman spectroscopic procedures, and validation protocols for pharmaceutical testing.",
  topics: "Blend uniformity, raw material identification, drying endpoint detection",
  ctaText: "Browse Application Notes",
  href: "/ResourcesPage",
  iconName: "FileText"
}, {
  id: "tech-guides",
  title: "Technical Guides",
  category: "Hardware & Integration",
  count: "28+ Manuals",
  description: "Engineering specifications, optical setup guidelines, and GMP integration blueprints for PAT process analyzers.",
  topics: "Optical probe selection, hazardous area compliance, communication setups",
  ctaText: "Explore Technical Guides",
  href: "/ResourcesPage",
  iconName: "BookOpen"
}, {
  id: "case-studies",
  title: "Case Studies",
  category: "Field Deployments",
  count: "32+ Implementations",
  description: "Real-world manufacturing scenarios showing reduced cycle times and automated quality control across Ireland and the UK.",
  topics: "Continuous tablet manufacturing, solvent recovery, inline powder flow",
  ctaText: "View Case Studies",
  href: "/ResourcesPage",
  iconName: "CheckCircle2"
}, {
  id: "white-papers",
  title: "White Papers",
  category: "Regulatory & Science",
  count: "18+ In-Depth Reports",
  description: "Peer-reviewed insights into chemometric modelling, compliance standards, and analytical instrumentation trends.",
  topics: "ICH Q13 continuous manufacturing, multivariate calibration, PAT data lifecycle",
  ctaText: "Read White Papers",
  href: "/ResourcesPage",
  iconName: "GraduationCap"
}];
export default function FormatsGrid() {
  const shouldReduceMotion = useReducedMotion();
  const getFormatIcon = (name: ContentFormatItem["iconName"]) => {
    switch (name) {
      case "FileText":
        return <FileText className="size-6 text-primary" />;
      case "BookOpen":
        return <BookOpen className="size-6 text-primary" />;
      case "CheckCircle2":
        return <CheckCircle2 className="size-6 text-primary" />;
      case "GraduationCap":
        return <GraduationCap className="size-6 text-primary" />;
    }
  };
  return <section id="contentformatsgrid" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Section */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium mb-4">
            <Sparkles className="size-3.5 text-primary" />
            <span>Technical Knowledge Base</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Analytical Resources by Format
          </h2>
          
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Explore our library of validated methods, engineering integration guides, deployment outcomes, and regulatory white papers tailored for regulated laboratories.
          </p>
        </div>

        {/* Content Formats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {contentFormats.map((item, index) => <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="p-3 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                      {getFormatIcon(item.iconName)}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary" className="font-normal">
                        {item.category}
                      </Badge>
                      <Badge variant="outline" className="text-muted-foreground border-border">
                        {item.count}
                      </Badge>
                    </div>
                  </div>

                  <div>
                    <CardTitle className="text-xl sm:text-2xl font-bold text-card-foreground font-default">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </div>
                </CardHeader>

                <CardContent className="pt-2">
                  <div className="p-3.5 rounded-md bg-secondary/50 border border-border/60">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                      Key Focus Areas:
                    </span>
                    <p className="text-xs sm:text-sm text-foreground/90 font-medium">
                      {item.topics}
                    </p>
                  </div>
                </CardContent>

                <CardFooter className="pt-4 border-t border-border/50">
                  <Button asChild variant="outline" className="w-full justify-between group">
                    <Link to={item.href}>
                      <span className="font-medium text-foreground">{item.ctaText}</span>
                      <ArrowRight className="size-4 text-primary transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Banner */}
        <div className="mt-12 sm:mt-16 p-6 sm:p-8 rounded-xl bg-secondary border border-border text-center flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left space-y-1 max-w-xl">
            <h3 className="text-lg font-semibold text-secondary-foreground font-default">
              Need a specific application study or custom feasibility test?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our technical specialists provide tailored feasibility testing and chemometric model development for your formulations.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild variant="default" className="w-full sm:w-auto">
              <Link to="/FeasibilityStudiesPage">
                <span>Request Feasibility Study</span>
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to="/DiscussYourApplicationPage">
                <span>Speak with an Specialist</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}